import { DestroyRef, EnvironmentInjector, Injectable, Injector, NgModuleRef, Type, ViewContainerRef } from '@angular/core';
import { DialogLike, DialogRef } from '@pxl/models/common';
import { ComponentProperties, DialogMap, DialogService } from '@pxl/models/contracts';
import { Subject } from 'rxjs';

@Injectable()
export class DefaultDialogService extends DialogService {
    public viewContainerRef?: ViewContainerRef;
    public onOpenDialog: Subject<DialogMap> = new Subject<DialogMap>();

    public registerViewContainer(vc: ViewContainerRef) {
        this.viewContainerRef = vc;
    }

    /**
     *
     * @param componentType The Dialog Component to open
     * @param inputs Only supply inputs, the type allows every public property, but those won't work
     * @param parentDestroyRef Destroy Ref of the parent component - optional, will take care of removing subscription on parent destruction
     * @param options See viewContainerRef.createComponent
     */
    public open<T extends DialogLike>(
        componentType: Type<T>,
        inputs: ComponentProperties<T> = {},
        parentDestroyRef?: DestroyRef | null,
        options?: {
            index?: number;
            injector?: Injector;
            ngModuleRef?: NgModuleRef<unknown>;
            environmentInjector?: EnvironmentInjector | NgModuleRef<unknown>;
            projectableNodes?: Node[][];
        },
    ) {
        if (!this.viewContainerRef) throw new Error('viewContainerRef not registered, failed to open DialogComponent');

        const componentRef = this.viewContainerRef.createComponent(componentType, options);
        const uuid = this.addToStack(componentRef);

        const dialogReference = new DialogRef(componentRef, parentDestroyRef);
        (dialogReference as unknown as DialogRef<DialogLike>).events.closed.subscribe(() => {
            this.removeFromStack(uuid);
            componentRef.destroy();
        });

        Object.entries(inputs).forEach(([name, value]) => {
            componentRef.setInput(name, value);
        });

        componentRef.setInput('hideBackdrop', true);

        setTimeout(() => {
            componentRef.instance.open();
            componentRef.changeDetectorRef.detectChanges();
        });

        return dialogReference;
    }

    public closeAll() {
        if (!this.viewContainerRef) return;
        this.viewContainerRef.clear();
    }
}
